/**
 * SCR-02 street map (MT-01): one journey up through every city. The home city's street is at
 * the bottom and the others stack above it in progression order, each a segment: its street
 * with 5 shops (progress rings, locked shops as silhouettes with the stars needed), its level
 * path with the pulsing current node, and a name banner where the journey enters it. A city the
 * player cannot visit yet is a teaser: a silhouette street, a lock and what opens it. The
 * playable city at the stage's centre is the active one (`app().city`, kept across restarts):
 * Play, My shop and the HUD follow it, and a pinned header names the city in view. Only the
 * segments within about a screen of the view exist, and a city's atlases load as its segment
 * comes near. Also runs Flow A's map reveal and Flow D's returning popups, and the big Play
 * button (DOM). Each street is a portrait road (bottom = its first level); the camera scrolls
 * vertically, with a glide after a fling.
 */
import * as Phaser from 'phaser';
import { cities, cityShops, economy, type ShopDef } from '../config/content.js';
import { app, type AppContext } from '../game/context.js';
import { safeInsets } from '../game/layout.js';
import { pathAt, pathLength, streetPlan, type Pt, type StreetPlan } from '../game/shopLayout.js';
import { C, FONT, css } from '../game/theme.js';
import { vp } from '../game/viewport.js';
import * as J from '../game/worldLayout.js';
import { deco, dur, wait } from '../fx/motion.js';
import { has, t } from '../i18n/index.js';
import { MARK, hasMark, mark, pendingMapPrompt } from '../services/firstSession.js';
import {
  activeShop,
  currentLevelNumber,
  highestCompleted,
  isCityOpen,
  isShopComplete,
  isShopUnlocked,
  levelId,
  nextUpgrade,
  upgradeCount,
  upgradeState,
} from '../services/progress.js';
import { adFailureToast } from '../services/ads.js';
import { claimDaily, collectOffline, dailyGift } from '../services/rewards.js';
import { op } from '../services/ops.js';
import { localDay, localDayKey } from '../services/time.js';
import { journeyHints, openView, railEntries, runMapHooks } from '../features/registry.js';
import { shopTitle } from '../ui/hud.js';
import { CityHeader } from '../views/cityHeader.js';
import { everyCityDone } from '../services/cityRoles.js';
import { hasCityOutfit, outfitFor } from '../services/cityOutfit.js';
import type { PlayerState } from '../services/state.js';
import { loadGlyphs } from '../game/glyphs.js';
import { BAND_CSS, drawBand, drawBanner, drawSilhouette, drawSurroundings, type SegGeo } from '../views/journey.js';
import { PipView } from '../views/pip.js';
import { HandPointer } from '../views/widgets.js';
import { fadeIn, go, onResizeSettled } from './nav.js';

/** A city's display name (translated when content has it); `fallback` names a city the catalog does not know yet. */
export function cityName(id: string, fallback?: string): string {
  return has(`city.${id}.name`) ? t(`city.${id}.name`) : (cities.find((c) => c.id === id)?.name ?? fallback ?? id);
}

/** The journey's cities as they are for this player now (home at the bottom). */
export function journeyNow(a: AppContext): J.Stop[] {
  const st = a.services.store.state;
  const levels = a.services.levels;
  const packs = new Set(levels.externalCities());
  const facts = cities.filter((c) => cityShops(c.id).length).map((c) => ({ id: c.id, order: c.order, pack: packs.has(c.id), needsUpdate: packs.has(c.id) && levels.needsUpdate(c.id) }));
  return J.journeyStops(st.city, facts, (city, prev) => isCityOpen(st, city, prev), journeyHints(a).map((h) => h.city));
}

/** The city the map opens on (boot, scene entry): `want` while the player can be there, else see J.resumeCity. */
export function startCity(a: AppContext, want: string | null, stops: readonly J.Stop[] = journeyNow(a)): string {
  const st = a.services.store.state;
  // levels still to beat: the catalog knows every city's last level, before any level index loads
  return J.resumeCity(stops, want, (city) => highestCompleted(st, city) < (cityShops(city).at(-1)?.levels[1] ?? 0));
}

/**
 * How long the view rests on a new active city before the map's music goes over to that city's
 * tune: long enough that a fling through several cities restarts no loop, short enough that the
 * player hears the city they stopped on almost at once.
 */
const TUNE_MS = 400;

export interface MapData {
  reveal?: boolean;
  /** a feature view to open once the map is up (level-up "Try it", dev `?open=`) */
  open?: string;
  openProps?: unknown;
  /** dev `?open=`: skip this visit's popups and hooks so the view is the only thing on screen */
  quiet?: boolean;
  /** the next-city card's "Visit": the map glides up to this city first */
  focus?: string;
}

interface ShopSpot {
  shop: ShopDef;
  /** facade centre (world px) */
  x: number;
  y: number;
  w: number;
  h: number;
  unlocked: boolean;
}

/** One city of the journey and, while it is near the view, its game objects. */
interface Segment {
  stop: J.Stop;
  city: string;
  name: string;
  /** the banner's line: the level range, or what opens the city */
  line: string;
  plan: StreetPlan;
  /** street plan → world scale, and the street's height (world px) */
  k: number;
  streetH: number;
  /** level range; 0 when unknown (a pack not on the device) */
  first: number;
  last: number;
  /** the view a teaser opens when tapped (the feature that offered it) */
  open: string | null;
  /** its street and shop fronts are in (until then a playable city draws its silhouette) */
  art: boolean;
  /** built objects; null while the segment is far from the view */
  objs: Phaser.GameObjects.GameObject[] | null;
  spots: ShopSpot[];
  banner: { x: number; y: number; w: number; h: number } | null;
}

export class MapScene extends Phaser.Scene {
  private segs: Segment[] = [];
  private world: J.World | null = null;
  private stage: J.Stage = { top: 0, bottom: 0 };
  /** per segment: the player can be there (the active city is one of these) */
  private playable: boolean[] = [];
  private plans = new Map<string, StreetPlan>();
  /** cities whose map art is on its way */
  private mapArt = new Set<string>();
  /** cities whose scene art (loadRest) is in, so Play starts at once */
  private restIn = new Set<string>();
  /** the city the map opened on (Pip stands on its current node), the active one, the one in view */
  private entry = 0;
  private active = 0;
  private inView = -1;
  /** camera scroll the segments were last synced for */
  private lastY = Number.NaN;
  private header: CityHeader | null = null;
  /** the segment being built: its objects are collected here (see own) */
  private building: Phaser.GameObjects.GameObject[] | null = null;
  /** each run (create) and each pending relayout gets a number; late async work checks it */
  private run = 0;
  private relays = 0;
  private reveal = false;
  private x0 = 0;
  private colW = 0;
  private bandH = 0;
  private topPad = 0;
  private bottomPad = 0;
  /** the node Pip stands on: the current level of the city the map opened on */
  private currentNode: { x: number; y: number } | null = null;
  private drag: { y: number; scroll: number; moved: boolean; at: number; t: number; v: number } | null = null;
  /** glide after a fling (camera px per ms; 0 when still) */
  private fling = 0;
  /** the player has scrolled or tapped the map this visit */
  private touched = false;
  /** the city the map was last on, when it is not here yet (a city pack registers after the map shows): the map goes there when it arrives, unless the player moved first */
  private follow: string | null = null;
  /** the city last reported as switched to, and the scene time the view last moved (one switch_city per settled change) */
  private reported = '';
  private movedAt = 0;
  /** the city whose map tune plays (the active one, once the view has rested on it for TUNE_MS) */
  private tuned = '';
  /** Pip on the map and the city he stands in (the entry city's current level) */
  private pip: { view: PipView; city: string } | null = null;

  private clampScroll(y: number): number {
    return this.world ? J.clampScroll(this.world, this.stage, y) : y;
  }

  /** Camera scroll that centres world-y in the visible stage. */
  private focusScroll(worldY: number): number {
    return this.world ? J.focusScroll(this.world, this.stage, worldY) : 0;
  }
  private popupsRunning = false;
  private openAfter: string | null = null;
  private openProps: unknown = {};
  private quiet = false;
  private focus: string | null = null;
  /** every city's last level is beaten: the top segment's banner opens the end-of-content card */
  private everyDone = false;

  /** built and showing (test hooks) */
  ready = false;

  constructor() {
    super('map');
  }

  init(data: MapData): void {
    this.ready = false;
    this.reveal = !!data?.reveal;
    this.openAfter = data?.open ?? null;
    this.openProps = data?.openProps ?? {};
    this.quiet = !!data?.quiet;
    this.focus = data?.focus ?? null;
    this.segs = [];
    this.world = null;
    this.plans = new Map();
    this.mapArt = new Set();
    this.inView = -1;
    this.lastY = Number.NaN;
    this.header = null;
    this.currentNode = null;
    this.drag = null;
    this.fling = 0;
    this.touched = false;
    this.follow = null;
    this.reported = '';
    this.movedAt = 0;
    this.tuned = '';
    this.pip = null;
    this.run++;
    this.relays++;
    this.popupsRunning = false;
  }

  create(): void {
    const a = app();
    a.ui.setBusy(true);
    const offPacks = a.services.levels.onChange(() => this.relayout());
    this.events.once('shutdown', offPacks);
    // leaving the map (Play, a shop) settles a switch at once
    this.events.once('shutdown', () => this.reportSwitch(true));
    void this.start(this.run);
    this.tune();
    onResizeSettled(this, () => {
      if (!a.ui.top) this.scene.restart({ reveal: false });
    });
  }

  /** The entry city's art, the streets' geometry and the open cities' level indexes, then the build. */
  private async start(run: number): Promise<void> {
    const a = app();
    let stops = journeyNow(a);
    // the city the player is in, while they can still be there (else the resume rule's pick)
    a.city = startCity(a, a.city, stops);
    // level indexes of the open cities (their ranges); one that cannot load now must not hold up the map
    const packs = stops.filter((s) => s.kind === 'play').map((s) => a.services.levels.loadPack(s.city).catch(() => null));
    // canvas text keeps the face it was drawn with: the Vietnamese fallback first when a name needs it
    await Promise.all([a.art.loadRest(a.city), this.loadPlans(stops), loadGlyphs(this.stopNames(stops)), ...packs]);
    // a city pack may have registered meanwhile
    stops = journeyNow(a);
    a.city = startCity(a, a.city, stops);
    await this.loadPlans(stops);
    if (run !== this.run || !this.scene.isActive()) return;
    a.ui.setBusy(false);
    this.restIn.add(a.city);
    this.build(stops);
    fadeIn(this);
    void this.afterShow();
  }

  /**
   * Whose street geometry a city uses: its own, or the home city's while it is a pack not on
   * the device (no shops yet). Plans are kept by that city, so a pack that registers later gets
   * its own plan instead of the one it borrowed.
   */
  private planCity(city: string): string {
    return cityShops(city).length ? city : app().services.store.state.city;
  }

  /**
   * Every name the journey draws into canvas text for these stops: each city's (a pack the catalog
   * does not know yet by its hint's name), the city a teaser waits for, and the shops' titles.
   */
  private stopNames(stops: readonly J.Stop[]): string[] {
    const a = app();
    const hints = new Map(journeyHints(a).map((h) => [h.city, h.name]));
    return stops.flatMap((s) => [
      cityName(s.city, hints.get(s.city)),
      ...(s.after ? [cityName(s.after)] : []),
      ...cityShops(s.city).map((x) => shopTitle(a.services.store.state, x.id, x.name)),
    ]);
  }

  private async loadPlans(stops: readonly J.Stop[]): Promise<void> {
    await Promise.all(
      stops.map(async ({ city }) => {
        const own = this.planCity(city);
        if (!this.plans.has(own)) this.plans.set(own, await streetPlan(own, cityShops(own).map((s) => s.id)));
      }),
    );
  }

  /** Segments and world for these stops, anchored on the entry city. */
  private setStops(stops: readonly J.Stop[], entryCity: string): void {
    const a = app();
    const hints = new Map(journeyHints(a).map((h) => [h.city, h]));
    this.everyDone = everyCityDone(a.services.store.state);
    this.segs = stops.map((stop, i) => {
      const plan = this.plans.get(this.planCity(stop.city))!;
      const k = this.colW / plan.w;
      const shops = cityShops(stop.city);
      const range = stop.kind === 'play' || stop.kind === 'update' ? a.services.levels.range(stop.city) : { first: shops[0]?.levels[0] ?? 0, last: shops.at(-1)?.levels[1] ?? 0 };
      const name = cityName(stop.city, hints.get(stop.city)?.name);
      const line =
        stop.kind === 'locked'
          ? t('map.cityLocked', { city: cityName(stop.after!) })
          : stop.kind === 'download'
            ? t('map.cityDownload')
            : stop.kind === 'update'
              ? t('map.cityUpdate')
              : this.everyDone && i === stops.length - 1
                ? t('map.everyCity')
                : t('map.cityLevels', { first: range.first, last: range.last });
      return {
        stop,
        city: stop.city,
        name,
        line,
        plan,
        k,
        streetH: plan.h * k,
        first: range.first,
        last: range.last,
        open: hints.get(stop.city)?.open ?? null,
        art: stop.kind !== 'play' || a.art.has(`env_${stop.city}_street_day`),
        objs: null,
        spots: [],
        banner: null,
      };
    });
    this.entry = Math.max(0, this.segs.findIndex((s) => s.city === entryCity && s.stop.kind === 'play'));
    this.active = this.entry;
    this.playable = this.segs.map((s) => s.stop.kind === 'play');
    this.world = J.layoutWorld(this.segs.map((s) => s.streetH + this.bandH), this.entry);
    this.inView = -1;
    this.lastY = Number.NaN;
  }

  private build(stops: readonly J.Stop[]): void {
    const a = app();
    const { width: W, height: H } = this.scale;
    const u = vp.u;
    const safe = safeInsets();
    this.colW = Math.min(W, H * 0.7);
    this.x0 = (W - this.colW) / 2;
    this.bandH = BAND_CSS * u;
    this.topPad = (safe.top + 60) * u;
    this.bottomPad = (safe.bottom + 128) * u;
    // the visible stage sits between the DOM top bar and the Play button row
    this.stage = { top: this.topPad, bottom: H - this.bottomPad };
    this.setStops(stops, a.city);
    // the pinned header sits under the top bar and clears the feature rail on both sides: centred, it
    // is as narrow on the left as on the right, where the rail sits inside the safe-area inset
    this.header = new CityHeader(this, a.art, W / 2, (safe.top + 64) * u, W - 2 * J.railClearance(Math.max(safe.left, safe.right)) * u);
    // the entry city first: Pip takes its current node, and the camera starts there
    const entry = this.segs[this.entry]!;
    this.buildSegment(entry);
    const at = this.currentNode?.y ?? this.world!.tops[this.entry]! + entry.streetH / 2;
    this.cameras.main.scrollY = this.focusScroll(at);
    // the next start opens here, unless the map fell back from a city that is not here yet
    const last = a.services.store.state.lastCity;
    if (last && last !== a.city && !this.playable.some((p, i) => p && this.segs[i]!.city === last)) this.follow = last;
    else this.remember(a.city);
    this.reported = a.city;
    this.syncView();
    this.wireInput();
    this.setHud();
    this.ready = true;
  }

  /**
   * The map's music in the active city (launch plan Phase 1 step 6): its own map loop when its
   * pack brought a theme, else the default (services/audio.ts). update() calls it once the view
   * has rested on a new active city for TUNE_MS, so a fling through several cities does not
   * restart the loop in each of them.
   */
  private tune(): void {
    const a = app();
    if (this.tuned === a.city) return;
    this.tuned = a.city;
    a.services.audio.music('map', a.city);
  }

  /** Keep the city the player is in for the next start (PlayerState.lastCity). */
  private remember(city: string): void {
    const store = app().services.store;
    if (store.state.lastCity !== city) store.update((st) => (st.lastCity = city));
  }

  /** switch_city once the active city has held for SETTLE_MS after the view stopped moving (`now`: the player left the map with it). */
  private reportSwitch(now: boolean, time = 0): void {
    const a = app();
    if (!this.reported) return;
    const city = J.settledSwitch(a.city, this.reported, now ? J.SETTLE_MS : time - this.movedAt);
    if (!city) return;
    this.reported = city;
    a.services.analytics.track('feature_action', { feature: 'cityPacks', action: 'switch_city', city });
  }

  /** Keep an object with the segment being built (it goes when the segment does). */
  private own = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
    this.building!.push(o);
    return o;
  };

  private geo(i: number): SegGeo {
    const s = this.segs[i]!;
    return { W: this.scale.width, x0: this.x0, colW: this.colW, k: s.k, top: this.world!.tops[i]!, streetH: s.streetH, bandH: this.bandH };
  }

  private buildSegment(seg: Segment): void {
    const a = app();
    const i = this.segs.indexOf(seg);
    const g = this.geo(i);
    const play = seg.stop.kind === 'play';
    seg.objs = this.building = [];
    seg.spots = [];
    // sky above the top of the world; grass under the Play row
    drawSurroundings(this, this.own, g, i === this.segs.length - 1 ? this.topPad * 2 : 0, i === 0 ? this.bottomPad * 2 : 0);
    if (play && seg.art) {
      const key = a.art.has(`env_${seg.city}_street_day`) ? `env_${seg.city}_street_day` : 'env_ny_street_day';
      const street = a.art.get(key);
      this.own(this.add.image(this.x0, g.top, street.texture, street.frame).setOrigin(0, 0).setDepth(1)).setDisplaySize(this.colW, seg.streetH);
    } else drawSilhouette(this, this.own, a.art, g, seg.plan, !play);
    if (play) {
      this.drawShops(seg, g.top);
      this.drawLevelPath(seg, g.top);
    } else if (seg.first > 0) this.drawTeaserNodes(seg, g.top);
    drawBand(this, this.own, g, i === 0);
    seg.banner = drawBanner(this, this.own, a.art, g, { name: seg.name, sub: seg.line, locked: !play });
    this.building = null;
    if (play && !seg.art) this.loadMapArt(seg.city);
  }

  private dropSegment(seg: Segment): void {
    if (!seg.objs) return;
    for (const o of seg.objs) {
      this.tweens.killTweensOf(o);
      o.destroy();
    }
    seg.objs = null;
    seg.spots = [];
    seg.banner = null;
  }

  /** A city's street and shop fronts as its segment comes near; its segment redraws with them. */
  private loadMapArt(city: string): void {
    if (this.mapArt.has(city)) return;
    this.mapArt.add(city);
    const a = app();
    const run = this.run;
    void Promise.all([a.art.ensureGroup(`env_${city}_street_day`), a.art.ensureGroup(`facades_${city}`)]).then(() => {
      if (run !== this.run || !this.scene.isActive()) return;
      for (const s of this.segs) {
        if (s.city !== city || s.art) continue;
        s.art = true;
        if (!s.objs) continue;
        this.dropSegment(s);
        this.buildSegment(s);
      }
    });
  }

  /**
   * After a scroll: build the segments within a screen of the view, drop those past a screen
   * and a half, and follow the city at the stage's centre (header, active city). Allocation-free
   * unless a segment comes or goes.
   */
  private syncView(): void {
    const w = this.world!;
    const y = this.cameras.main.scrollY;
    this.lastY = y;
    const H = this.scale.height;
    const lo = J.segmentAt(w, y + H * 2);
    const hi = J.segmentAt(w, y - H);
    const keepLo = J.segmentAt(w, y + H * 2.5);
    const keepHi = J.segmentAt(w, y - H * 1.5);
    for (let i = 0; i < this.segs.length; i++) {
      const s = this.segs[i]!;
      if (i >= lo && i <= hi) {
        if (!s.objs) this.buildSegment(s);
      } else if ((i < keepLo || i > keepHi) && s.objs) this.dropSegment(s);
    }
    const centre = J.stageCentre(this.stage, y);
    const at = J.segmentAt(w, centre);
    if (at !== this.inView) {
      this.inView = at;
      const s = this.segs[at]!;
      this.header?.show(s.name, s.stop.kind !== 'play');
    }
    const act = J.activeSegment(w, this.playable, centre, this.active);
    if (act !== this.active) this.activate(act);
  }

  /** The playable city at the centre becomes the one Play, My shop and the HUD are about. */
  private activate(i: number): void {
    const a = app();
    this.active = i;
    const city = this.segs[i]!.city;
    if (city === a.city) return;
    a.city = city;
    this.remember(city);
    // its shops and rounds are next: their art starts now (Play waits for it if it must)
    void a.art.loadRest(city).then(() => this.restIn.add(city));
    this.setHud();
  }

  override update(time: number, dt: number): void {
    if (!this.world || !this.ready) return;
    const cam = this.cameras.main;
    if (this.fling) {
      const next = cam.scrollY + this.fling * dt;
      const y = this.clampScroll(next);
      this.fling = y !== next ? 0 : J.glide(this.fling, dt);
      if (Math.abs(this.fling) < J.GLIDE_STOP * vp.u) this.fling = 0;
      cam.scrollY = y;
    }
    // a finger on the map counts as moving: a switch settles once the view rests
    const moved = cam.scrollY !== this.lastY;
    if (moved) this.syncView();
    if (moved || this.drag) this.movedAt = time;
    else {
      this.reportSwitch(false, time);
      if (time - this.movedAt >= TUNE_MS) this.tune();
    }
  }

  /** A city pack registered while the map is up: the journey is rebuilt around the city in view. */
  private relayout(): void {
    const n = ++this.relays;
    void Promise.resolve().then(async () => {
      if (!this.world) return; // not built yet: the build reads the packs then
      const a = app();
      const stops = journeyNow(a);
      // a new pack's names (a Hanoi pack's) get their face before the rebuild draws them
      await Promise.all([this.loadPlans(stops), loadGlyphs(this.stopNames(stops))]);
      if (n !== this.relays || !this.world || !this.scene.isActive()) return;
      const cam = this.cameras.main;
      const at = J.segmentAt(this.world, J.stageCentre(this.stage, cam.scrollY));
      const keep = this.segs[at]!.city;
      const offset = cam.scrollY - this.world.tops[at]!;
      const activeCity = a.city;
      for (const s of this.segs) this.dropSegment(s);
      this.setStops(stops, this.segs[this.entry]!.city);
      const act = this.segs.findIndex((s) => s.city === activeCity && s.stop.kind === 'play');
      if (act >= 0) this.active = act;
      else a.city = this.segs[this.active]!.city;
      const j = this.segs.findIndex((s) => s.city === keep);
      const before = cam.scrollY;
      cam.scrollY = this.clampScroll((j >= 0 ? this.world.tops[j]! : 0) + offset);
      // a finger still on the map keeps its place in the new world
      if (this.drag) this.drag.scroll += cam.scrollY - before;
      // the city the map was last on has arrived: the map goes to its current level, unless the player moved first
      const f = this.follow ? this.segs.findIndex((s) => s.city === this.follow && s.stop.kind === 'play') : -1;
      if (f >= 0) {
        this.follow = null;
        if (!this.touched) {
          this.reported = this.segs[f]!.city; // a return, not a switch
          cam.scrollY = this.focusScroll(this.nodeY(f));
        }
      }
      this.syncView();
      this.setHud();
    });
  }

  /** World y of a playable city's current level node. */
  private nodeY(i: number): number {
    const seg = this.segs[i]!;
    const cur = currentLevelNumber(app().services.store.state, seg.last, seg.city, seg.first);
    const p = pathAt(seg.plan.path as Pt[], Math.max(40, this.levelDistance(seg.plan, seg.city, cur, seg.last)));
    return this.world!.tops[i]! + p.y * seg.k;
  }

  private drawShops(seg: Segment, top: number): void {
    const a = app();
    const st = a.services.store.state;
    const plan = seg.plan;
    cityShops(seg.city).forEach((shop) => {
      const plot = plan.plots[shop.id];
      if (!plot) return;
      const unlocked = isShopUnlocked(st, shop.id);
      const fw = plan.facade.w * plot.scale * seg.k;
      const fh = plan.facade.h * plot.scale * seg.k;
      const x = this.x0 + plot.x * seg.k;
      const y = top + plot.y * seg.k;
      if (seg.art) {
        const key = shop.id.replace(/^([a-z]+)_/, 'shp_$1_') + (unlocked ? '_facade' : '_facade_locked');
        const f = a.art.get(key);
        this.own(this.add.image(x, y, f.texture, f.frame).setOrigin(0.5, 1).setDisplaySize(fw, fh).setDepth(10 + (y - top) * 0.001));
      }
      seg.spots.push({ shop, x, y: y - fh / 2, w: fw, h: fh, unlocked });
      this.decorateShop(shop, x, y, fw, fh, unlocked);
    });
  }

  private decorateShop(shop: ShopDef, x: number, base: number, fw: number, fh: number, unlocked: boolean): void {
    const a = app();
    const st = a.services.store.state;
    const u = vp.u;
    const fs = Math.round(Math.max(13, Math.min(17, fw / u / 8)) * u);
    const name = unlocked ? shopTitle(st, shop.id, shop.name) : t('map.lockedShop');
    const label = this.own(
      this.add
        .text(x, base + 4 * u, name, {
          fontFamily: FONT,
          fontSize: `${fs}px`,
          fontStyle: '700',
          color: '#ffffff',
          backgroundColor: css(unlocked ? C.ink : 0x6d6078),
          padding: { x: Math.round(10 * u), y: Math.round(3 * u) },
        })
        .setOrigin(0.5, 0)
        .setDepth(30),
    );
    // long names (translations run ~30% longer) stay on screen
    const W = this.scale.width;
    const half = label.width / 2;
    label.setX(Math.min(Math.max(label.x, half + 6 * u), W - half - 6 * u));
    if (!unlocked) {
      const star = a.art.get('ico_cur_star');
      const txt = this.own(
        this.add
          .text(x + 12 * u, base - fh * 0.2, t('map.starsToOpen', { stars: shop.unlockStars }), {
            fontFamily: FONT,
            fontSize: `${Math.round(19 * u)}px`,
            fontStyle: '700',
            color: '#ffffff',
            stroke: css(C.ink),
            strokeThickness: Math.round(5 * u),
          })
          .setOrigin(0.5)
          .setDepth(31),
      );
      this.own(this.add.image(txt.x - txt.width / 2 - 13 * u, txt.y, star.texture, star.frame).setDisplaySize(26 * u, 26 * u).setDepth(31));
      return;
    }
    // MT-01.1 progress ring
    const n = upgradeCount(st, shop.id);
    const total = shop.upgrades.length;
    const rx = x + fw * 0.46;
    const ry = base - fh * 0.9;
    const R = 20 * u;
    const g = this.own(this.add.graphics().setDepth(31));
    g.fillStyle(C.ink, 1);
    g.fillCircle(rx, ry + 3 * u, R + 5 * u);
    g.fillStyle(C.cream, 1);
    g.fillCircle(rx, ry, R + 4 * u);
    g.lineStyle(6 * u, 0xe8d6bd, 1);
    g.beginPath();
    g.arc(rx, ry, R - 1 * u, 0, Math.PI * 2);
    g.strokePath();
    if (n > 0) {
      g.lineStyle(6 * u, isShopComplete(st, shop) ? C.mustard : C.teal, 1);
      g.beginPath();
      g.arc(rx, ry, R - 1 * u, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * n) / total);
      g.strokePath();
    }
    this.own(
      this.add
        .text(rx, ry, `${n}/${total}`, { fontFamily: FONT, fontSize: `${Math.round(12 * u)}px`, fontStyle: '700', color: css(C.ink) })
        .setOrigin(0.5)
        .setDepth(32),
    );
    const next = nextUpgrade(st, shop.id);
    if (next && upgradeState(st, shop.id, next.slot).status === 'ready') {
      const bubble = this.own(
        this.add
          .text(x - fw * 0.42, base - fh * 0.9, '!', {
            fontFamily: FONT,
            fontSize: `${Math.round(22 * u)}px`,
            fontStyle: '700',
            color: '#ffffff',
            backgroundColor: css(C.tomato),
            padding: { x: Math.round(10 * u), y: Math.round(1 * u) },
          })
          .setOrigin(0.5)
          .setDepth(32),
      );
      const d = deco(500);
      if (d) this.tweens.add({ targets: bubble, y: bubble.y - 7 * u, duration: d, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }
  }

  /** Where level n sits on a city's road: between its shop's plot and the next one. */
  private levelDistance(plan: StreetPlan, city: string, n: number, total: number): number {
    const path = plan.path;
    const L = pathLength(path as Pt[]);
    const shops = cityShops(city);
    // arc distance of the path point nearest each plot
    const anchor = shops.map((s) => {
      const p = plan.plots[s.id];
      if (!p) return 0;
      let best = 0;
      let bestD = Infinity;
      let acc = 0;
      for (let i = 0; i < path.length; i++) {
        if (i > 0) acc += Math.hypot(path[i]![0] - path[i - 1]![0], path[i]![1] - path[i - 1]![1]);
        const d = Math.hypot(path[i]![0] - p.x, path[i]![1] - p.y);
        if (d < bestD) {
          bestD = d;
          best = acc;
        }
      }
      return best;
    });
    const idx = shops.findIndex((s) => n >= s.levels[0] && n <= s.levels[1]);
    if (idx < 0) return (L * Math.min(1, n / Math.max(1, total))) | 0;
    const s0 = anchor[idx]!;
    const s1 = anchor[idx + 1] ?? L;
    const span = shops[idx]!.levels[1] - shops[idx]!.levels[0] + 1;
    return s0 + ((n - shops[idx]!.levels[0]) / span) * Math.max(0, s1 - s0);
  }

  /** A pulse on a current-level node Pip is not standing on (another city's, or a rebuilt one). */
  private pulse(img: Phaser.GameObjects.Image): void {
    const d = deco(700);
    if (d) this.tweens.add({ targets: img, scale: img.scale * 1.14, duration: d, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
  }

  private drawLevelPath(seg: Segment, top: number): void {
    const a = app();
    const st = a.services.store.state;
    const u = vp.u;
    const plan = seg.plan;
    const { first, last: max } = seg;
    const cur = currentLevelNumber(st, max, seg.city, first);
    const L = pathLength(plan.path as Pt[]);
    const sCur = Math.max(40, this.levelDistance(plan, seg.city, cur, max));
    const step = (74 * u) / seg.k;
    const nodes: { n: number; x: number; y: number }[] = [];
    for (let d = -3; d <= 4; d++) {
      const n = cur + d;
      if (n < first || n > max) continue;
      const s = sCur + d * step;
      if (s < 0 || s > L) continue;
      const p = pathAt(plan.path as Pt[], s);
      nodes.push({ n, x: this.x0 + p.x * seg.k, y: top + p.y * seg.k });
    }
    const g = this.own(this.add.graphics().setDepth(20));
    g.fillStyle(0xffffff, 0.9);
    for (let i = 0; i < nodes.length - 1; i++) {
      const p = nodes[i]!;
      const q = nodes[i + 1]!;
      for (let f = 0.25; f < 0.8; f += 0.25) g.fillCircle(p.x + (q.x - p.x) * f, p.y + (q.y - p.y) * f, 3.5 * u);
    }
    for (const node of nodes) {
      const best = st.levels[levelId(seg.city, node.n)]?.bestStars ?? 0;
      const here = node.n === cur;
      // Pip stands on the current node of the city the map opened on, once per visit
      const isCur = here && !this.currentNode && seg === this.segs[this.entry];
      const key = here ? 'ui_map_node_current' : node.n < cur ? 'ui_map_node' : 'ui_map_node_locked';
      const f = a.art.get(key);
      const size = (here ? 58 : 44) * u;
      const img = this.own(this.add.image(node.x, node.y, f.texture, f.frame).setDisplaySize(size, size).setDepth(21));
      this.own(
        this.add
          .text(node.x, node.y - 1 * u, String(node.n), {
            fontFamily: FONT,
            fontSize: `${Math.round((here ? 22 : 17) * u)}px`,
            fontStyle: '700',
            color: '#ffffff',
            stroke: css(C.ink),
            strokeThickness: Math.round(4 * u),
          })
          .setOrigin(0.5)
          .setDepth(22),
      );
      if (best > 0) {
        const sf = a.art.get('ui_star_full');
        for (let s = 0; s < best; s++)
          this.own(
            this.add
              .image(node.x + (s - (best - 1) / 2) * 14 * u, node.y + size * 0.56, sf.texture, sf.frame)
              .setDisplaySize(16 * u, 16 * u)
              .setDepth(23),
          );
      }
      if (here && !isCur) this.pulse(img);
      if (isCur) {
        this.currentNode = { x: node.x, y: node.y };
        const d = deco(700);
        if (d) this.tweens.add({ targets: img, scale: img.scale * 1.14, duration: d, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
        // MT-09.1: Pip wears the equipped costume and look on the map too, and re-dresses when they
        // change. With none equipped he wears the city's own outfit (launch plan Phase 1 step 6,
        // services/cityOutfit.ts) while Settings › City outfits is on
        const pip = new PipView(this, node.x, node.y - size * 0.3, 64 * u);
        pip.sprite.setDepth(24);
        this.pip = { view: pip, city: seg.city };
        const dress = (s: PlayerState) => {
          void pip.wear(outfitFor(s, seg.city, (c) => hasCityOutfit(a.art, c)), a.art);
          void pip.look(s.skin);
        };
        dress(st);
        this.events.once('shutdown', a.services.store.subscribe(dress));
      }
    }
  }

  /** A teaser's first levels, locked: the numbering visibly carries on into the city. */
  private drawTeaserNodes(seg: Segment, top: number): void {
    const a = app();
    const u = vp.u;
    const f = a.art.get('ui_map_node_locked');
    const step = (74 * u) / seg.k;
    const s0 = Math.max(40, this.levelDistance(seg.plan, seg.city, seg.first, seg.last));
    for (let d = 0; d < 3 && seg.first + d <= seg.last; d++) {
      const p = pathAt(seg.plan.path as Pt[], s0 + d * step);
      const x = this.x0 + p.x * seg.k;
      const y = top + p.y * seg.k;
      this.own(this.add.image(x, y, f.texture, f.frame).setDisplaySize(44 * u, 44 * u).setDepth(21));
      this.own(
        this.add
          .text(x, y - 1 * u, String(seg.first + d), { fontFamily: FONT, fontSize: `${Math.round(17 * u)}px`, fontStyle: '700', color: '#ffffff', stroke: css(C.ink), strokeThickness: Math.round(4 * u) })
          .setOrigin(0.5)
          .setDepth(22),
      );
    }
  }

  private wireInput(): void {
    const cam = this.cameras.main;
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      app().services.audio.unlock();
      // a touch that stops a glide is not a tap on whatever glided under the finger
      const stopped = Math.abs(this.fling) > J.GLIDE_MIN * vp.u;
      this.fling = 0;
      this.touched = true;
      this.drag = { y: p.y, scroll: cam.scrollY, moved: stopped, at: p.y, t: p.downTime, v: 0 };
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      const d = this.drag;
      if (!d || !p.isDown) return;
      const dy = p.y - d.y;
      if (Math.abs(dy) > 10 * vp.u) d.moved = true;
      if (!d.moved) return;
      cam.scrollY = this.clampScroll(d.scroll - dy);
      // the finger's speed (camera px per ms, from the events' own times), smoothed, for the glide after a fling
      if (p.moveTime > d.t) d.v = d.v * 0.2 - ((p.y - d.at) / (p.moveTime - d.t)) * 0.8;
      d.at = p.y;
      d.t = p.moveTime;
    });
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      const d = this.drag;
      this.drag = null;
      if (!d) return;
      if (d.moved) {
        // a fling (a finger still moving when it lifts) glides on; reduced motion stops at once
        const max = J.GLIDE_MAX * vp.u;
        if (p.upTime - d.t < 80 && Math.abs(d.v) > J.GLIDE_MIN * vp.u && deco(1)) this.fling = Math.max(-max, Math.min(max, d.v));
        return;
      }
      if (this.popupsRunning) return;
      const wy = p.y + cam.scrollY;
      for (let i = 0; i < this.segs.length; i++) {
        const s = this.segs[i]!;
        if (!s.objs) continue;
        const spot = s.spots.find((x) => Math.abs(p.x - x.x) < x.w / 2 && Math.abs(wy - x.y) < x.h / 2);
        if (spot) {
          // entering a shop of the city next to the active one makes that city the active one
          if (spot.unlocked && i !== this.active) this.activate(i);
          this.openShop(spot);
          return;
        }
        const b = s.banner;
        if (b && s.stop.kind !== 'play' && Math.abs(p.x - b.x) < b.w / 2 && Math.abs(wy - b.y) < b.h / 2) {
          this.tapTeaser(s);
          return;
        }
        // every city done: the journey's last banner brings the end-of-content card back
        if (b && this.everyDone && i === this.segs.length - 1 && Math.abs(p.x - b.x) < b.w / 2 && Math.abs(wy - b.y) < b.h / 2) {
          app().services.audio.play('button');
          void openView(app(), 'endOfContent', { from: 'map' }).then(() => this.setHud());
          return;
        }
      }
    });
    this.input.on('wheel', (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
      this.fling = 0;
      this.touched = true;
      cam.scrollY = this.clampScroll(cam.scrollY + dy * vp.u);
    });
  }

  private openShop(spot: ShopSpot): void {
    const a = app();
    if (!spot.unlocked) {
      a.services.audio.play('nope');
      a.ui.toast(t('map.needStars', { stars: spot.shop.unlockStars }));
      return;
    }
    a.services.audio.play('button');
    this.remember(spot.shop.city);
    go(this, 'shop', { shopId: spot.shop.id });
  }

  /** A teaser's banner: the feature that brings the city opens its screen; otherwise what opens it. */
  private tapTeaser(s: Segment): void {
    const a = app();
    if (s.stop.kind === 'download' && s.open) {
      a.services.audio.play('button');
      void openView(a, s.open, { from: 'map' }).then(() => this.setHud());
      return;
    }
    a.services.audio.play('nope');
    if (s.stop.kind === 'update') a.ui.toast(t('map.updateToVisit', { city: s.name }));
    else if (s.stop.kind === 'locked') a.ui.toast(t('map.cityLockedHint', { city: cityName(s.stop.after!), next: s.name }));
    else a.ui.toast(s.line);
  }

  private setHud(): void {
    // async flows (build animation, popups) may finish after the player left this scene
    if (!this.scene.isActive()) return;
    const a = app();
    const st = a.services.store.state;
    const { first, last: max } = a.services.levels.range(a.city);
    const shop = activeShop(st, a.city);
    const next = nextUpgrade(st, shop.id);
    a.ui.setHud({
      mode: 'map',
      play: null,
      shop: null,
      onStore: a.remote.flags.store !== false && a.services.api.enabled ? () => void openView(a, 'store', {}).then(() => this.setHud()) : null,
      onSettings: () => void openView(a, 'settings').then(() => this.setHud()),
      map: {
        level: currentLevelNumber(st, max, a.city, first),
        maxLevel: max,
        shopHint: !!next && upgradeState(st, shop.id, next.slot).status === 'ready',
        offline: a.services.sync.status === 'offline',
        rail: this.popupsRunning ? [] : railEntries(a, (kind) => void openView(a, kind).then(() => this.setHud())),
        onPlay: () => {
          if (this.popupsRunning) return;
          a.services.audio.unlock();
          a.services.audio.play('button');
          const city = a.city;
          this.remember(city);
          const start = () => {
            if (this.scene.isActive()) go(this, 'play', { level: currentLevelNumber(a.services.store.state, max, city, first) });
          };
          if (this.restIn.has(city)) return start();
          // a city the player just scrolled to: its items and customers first
          a.ui.setBusy(true);
          void a.art.loadRest(city).then(() => {
            a.ui.setBusy(false);
            this.restIn.add(city);
            start();
          });
        },
        onShop: () => {
          if (this.popupsRunning && !this.hintActive) return;
          const sp = this.segs[this.active]?.spots.find((x) => x.shop.id === activeShop(a.services.store.state, a.city).id);
          if (sp) this.openShop(sp);
        },
      },
    });
  }

  private hintActive = false;

  /**
   * Flow A reveal; Flow D popups (offline sales → daily gift); the age check once the first
   * session is done; then feature hooks (inbox deliveries, league results, Starter Pack,
   * web-to-app) and first-session prompts — one at a time.
   */
  private async afterShow(): Promise<void> {
    const a = app();
    const s = a.services;
    this.popupsRunning = true;
    this.setHud();
    if (this.reveal || !hasMark(s.store.state, MARK.mapRevealed)) {
      await this.runReveal();
      this.popupsRunning = false;
      this.setHud();
      return;
    }
    if (this.focus) await this.focusCity(this.focus);
    if (this.quiet && this.openAfter) {
      this.popupsRunning = false;
      this.setHud();
      const open = this.openAfter;
      this.openAfter = null;
      await openView(a, open, this.openProps).catch((e: unknown) => console.warn('[market-pop] ?open=', open, e));
      this.setHud();
      return;
    }
    // SY-02.1 outside the EU/UK: the age check at the first calm moment after Flow A
    if (s.privacy.needsAge && pendingMapPrompt(s.store.state) === null) await openView(a, 'privacyGate', { reason: 'map' });
    const today = localDay(s.now());
    if (!a.session.offlineShown && a.session.offlineCoins >= economy.offlineSales.minPopupCoins) {
      a.session.offlineShown = true;
      const coins = a.session.offlineCoins;
      const ad = !s.ads.enabled ? 'none' : s.ads.likely('doubleOffline') ? 'ad' : 'tryLater';
      const choice = await a.ui.open('offline', { coins, hours: a.session.offlineHours, ad });
      let nonce: string | undefined;
      if (choice === 'double') {
        const r = await s.ads.watch('doubleOffline');
        if (r.ok) nonce = r.nonce;
        else {
          const key = adFailureToast(r.reason);
          if (key) a.ui.toast(t(key));
        }
      }
      let paid = 0;
      s.store.update((st) => (paid = collectOffline(st, coins, s.now(), !!nonce)));
      if (paid > 0) s.sync.enqueue(op.offlineCollect(nonce));
      s.analytics.track('offline_collect', { coins: paid, doubled: !!nonce });
      s.audio.play('coin');
      a.ui.setHud({ coinPulse: a.ui.hud.coinPulse + 1 });
    }
    const gift = dailyGift(s.store.state, today);
    if (!a.session.dailyShown && gift.claimable) {
      a.session.dailyShown = true;
      await a.ui.open('daily', { day: gift.day, grant: gift.grant, streak: s.store.state.streak.count, gifts: economy.dailyGift });
      let claimed = false;
      s.store.update((st) => (claimed = claimDaily(st, today) !== null));
      if (claimed) s.sync.enqueue(op.dailyGift(localDayKey(s.now())));
      s.analytics.track('daily_gift', { day: gift.day });
      s.audio.play('coin');
      a.ui.setHud({ coinPulse: a.ui.hud.coinPulse + 1 });
    }
    await runMapHooks(a); // manifests' mapOrder: deliveries, results, offers, prompts
    if (pendingMapPrompt(s.store.state) === 'savePrompt10') {
      await openView(a, 'savePrompt', { again: true });
      s.store.update((st) => {
        mark(st, MARK.savePrompt10);
        st.flags.savePrompts++;
      });
    }
    this.popupsRunning = false;
    this.setHud();
    const open = this.openAfter;
    this.openAfter = null;
    if (open) await openView(a, open, this.openProps).catch((e: unknown) => a.flags.dev && console.warn('[market-pop] open', open, e));
    // a level-up "Try it" held while a city moment routed the player elsewhere (PlayScene onWon)
    const feature = a.session.featureAfter;
    a.session.featureAfter = null;
    if (feature && feature !== open && this.scene.isActive()) await openView(a, feature, {}).catch((e: unknown) => a.flags.dev && console.warn('[market-pop] open', feature, e));
    this.setHud();
  }

  /**
   * The next-city card's "Visit": the camera glides up the journey to the city (its current level
   * when it plays, its banner when it is a teaser). A city whose pack is not on this device opens
   * City Packs, which starts (or queues) its download as the journey banner's screen does.
   */
  private async focusCity(city: string): Promise<void> {
    const a = app();
    this.focus = null;
    const i = this.segs.findIndex((s) => s.city === city);
    const seg = this.segs[i];
    if (seg) {
      const cam = this.cameras.main;
      const to = this.focusScroll(seg.stop.kind === 'play' ? this.nodeY(i) : this.world!.tops[i]! + seg.streetH + this.bandH / 2);
      const d = dur(1100);
      if (d) {
        this.tweens.add({ targets: cam, scrollY: to, duration: d, ease: 'Sine.easeInOut' });
        await wait(d);
      }
      cam.scrollY = to;
      this.syncView();
      if (seg.stop.kind === 'play') return;
      // still locked, or its pack needs a newer app: what a tap on its banner says
      if (seg.stop.kind !== 'download') {
        this.tapTeaser(seg);
        return;
      }
    }
    await openView(a, seg?.open ?? 'cityPacks', { from: 'nextCity', start: city }).catch(() => undefined);
    this.setHud();
  }

  /** Flow A step F: the camera travels down the street to the empty first shop. */
  private async runReveal(): Promise<void> {
    const a = app();
    const s = a.services;
    const cam = this.cameras.main;
    const home = this.segs[this.entry];
    const target = home?.spots[0];
    if (!home || !target) return;
    const d = dur(1400);
    const to = this.focusScroll(target.y);
    if (d) {
      cam.scrollY = this.focusScroll(this.world!.tops[this.entry]! + home.streetH * 0.3);
      this.tweens.add({ targets: cam, scrollY: to, duration: d, ease: 'Sine.easeInOut' });
      await wait(d);
    }
    cam.scrollY = to;
    s.store.update((st) => mark(st, MARK.mapRevealed));
    s.analytics.track('tutorial_step', { step: 'map_reveal' });
    this.hintActive = true;
    const hand = new HandPointer(this);
    hand.show(target.x + target.w * 0.08, target.y + target.h * 0.05);
    const u = vp.u;
    // On a phone the street runs under the feature rail: the hint stays on the screen and left of
    // it (at its pulse's widest), and wraps when a longer translation needs the room. The rail sits
    // inside the right safe-area inset, so the inset is part of what the hint keeps clear of.
    const { width: W } = this.scale;
    const rail = J.railClearance(safeInsets().right) * u;
    const edge = 6 * u;
    const padX = Math.round(14 * u);
    const PULSE = 1.06;
    const bubble = this.add
      .text(target.x, target.y - target.h * 0.72, t('map.tapShop'), {
        fontFamily: FONT,
        fontSize: `${Math.round(21 * u)}px`,
        fontStyle: '700',
        color: css(C.ink),
        backgroundColor: '#ffffff',
        padding: { x: padX, y: Math.round(8 * u) },
        align: 'center',
        wordWrap: { width: Math.max(80 * u, (W - rail - 2 * edge) / PULSE - 2 * padX), useAdvancedWrap: true },
      })
      .setOrigin(0.5)
      .setDepth(40);
    bubble.setX(J.clearOfRail(bubble.x, bubble.width * PULSE, W, rail, edge));
    const bd = deco(600);
    if (bd) this.tweens.add({ targets: bubble, scale: PULSE, duration: bd, yoyo: true, repeat: -1 });
  }

  /** The journey as the player sees it (test hooks): the header, the cities (kind, banner line, banner position in CSS px while built). */
  journey(): { header: string; inView: string; active: string; cities: { city: string; kind: J.StopKind; name: string; line: string; banner: { x: number; y: number } | null }[] } {
    const cam = this.cameras.main;
    return {
      header: this.header?.text ?? '',
      inView: this.segs[this.inView]?.city ?? '',
      active: this.segs[this.active]?.city ?? '',
      cities: this.segs.map((s) => ({
        city: s.city,
        kind: s.stop.kind,
        name: s.name,
        line: s.line,
        banner: s.banner ? { x: (s.banner.x - cam.scrollX) / vp.dpr, y: (s.banner.y - cam.scrollY) / vp.dpr } : null,
      })),
    };
  }

  /** Pip on the map (test hooks): the city he stands in, the costume he wears there and the one whose overlay is drawn (null before he is). */
  outfit(): { city: string; wearing: string; drawn: string | null } | null {
    return this.pip ? { city: this.pip.city, wearing: this.pip.view.wearing, drawn: this.pip.view.dressed } : null;
  }

  /** CSS-px screen positions of interactive canvas targets (test hooks). */
  targets(): Record<string, { x: number; y: number }> {
    const cam = this.cameras.main;
    const out: Record<string, { x: number; y: number }> = {};
    for (const seg of this.segs) for (const sp of seg.spots) out[sp.shop.id] = { x: (sp.x - cam.scrollX) / vp.dpr, y: (sp.y - cam.scrollY) / vp.dpr };
    return out;
  }
}
